import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type Variant = "dark" | "primary" | "light" | "outline";
type Size = "md" | "lg";

const variants: Record<Variant, string> = {
  // Bleu marine du logo : l'action principale du site public.
  dark: "bg-primary-950 text-white hover:bg-primary-900",
  primary: "bg-primary-600 text-white hover:bg-primary-700",
  light: "bg-white text-primary-950 hover:bg-primary-50",
  outline: "border border-background-300 bg-white text-foreground-800 hover:border-foreground-400",
};

const sizes: Record<Size, string> = {
  md: "h-11 px-5 text-sm",
  lg: "h-12 px-6 text-base",
};

interface SiteButtonProps {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  icon?: string;
  /** Lien interne (React Router) ; sinon `href` (ancre, mailto, wa.me) ; sinon bouton. */
  to?: string;
  href?: string;
  onClick?: () => void;
  className?: string;
  external?: boolean;
}

/** Bouton du site public (accueil, suivi) : forme pilule, enfoncement léger à l'appui. */
export default function SiteButton({
  children,
  variant = "dark",
  size = "md",
  icon,
  to,
  href,
  onClick,
  className = "",
  external,
}: SiteButtonProps) {
  const classes = `press inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold font-heading transition-colors focus-visible:outline-offset-2 cursor-pointer ${variants[variant]} ${sizes[size]} ${className}`;
  const content = (
    <>
      {icon && <i className={`${icon} text-[1.1em]`} aria-hidden="true"></i>}
      {children}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={classes}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
        {content}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={classes}>
      {content}
    </button>
  );
}
